import React, { useState, useEffect } from 'react';
import TagPicker from './TagPicker';
import { Tag } from '../../types';
import { tagService } from '../../services';
import Button from '../ui/Button';

export interface SetupWizardResult {
  name: string;
  description: string;
  tagIds: string[];
  expectedDurationDays?: number;
  requiresAsset?: boolean;
}

interface SetupWizardModalProps {
  type: 'dashboard' | 'workflow';
  onComplete: (result: SetupWizardResult) => void;
  onCancel: () => void;
}

const SetupWizardModal: React.FC<SetupWizardModalProps> = ({
  type,
  onComplete,
  onCancel,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [expectedDurationDays, setExpectedDurationDays] = useState<number | undefined>(undefined);
  const [requiresAsset, setRequiresAsset] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadTags = async () => {
    try {
      const data = await tagService.list();
      setTags(data);
    } catch (err) {
      console.error('Failed to load tags', err);
    }
  };

  useEffect(() => {
    loadTags();
  }, []);

  const label = type === 'dashboard' ? 'Dashboard' : 'Workflow';

  const clearErrors = () => { if (Object.keys(errors).length > 0) setErrors({}); };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = `${label} name is required`;
    if (!description.trim()) newErrors.description = 'Description is required';
    if (tagIds.length === 0) newErrors.tagIds = 'At least one tag is required';
    if (type === 'workflow' && (!expectedDurationDays || expectedDurationDays <= 0)) {
      newErrors.duration = 'Expected duration is required';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    onComplete({
      name: name.trim(),
      description: description.trim(),
      tagIds,
      ...(type === 'workflow' && { expectedDurationDays, requiresAsset }),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-surface rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <h2 className="text-lg font-semibold text-neutral-900">
            Create New {label}
          </h2>
          <p className="text-sm text-neutral-500 mt-1">
            Fill in the details to get started. You can always change these later.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">
              {label} Name <span className="text-danger-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={e => { setName(e.target.value); clearErrors(); }}
              className={`w-full px-3 py-2 text-sm border rounded-lg ${
                errors.name ? 'border-danger-400 bg-danger-50' : 'border-neutral-300'
              }`}
              placeholder={`Enter ${type} name`}
              autoFocus
            />
            {errors.name && <p className="text-xs text-danger-500 mt-1">{errors.name}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">
              Description <span className="text-danger-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={e => { setDescription(e.target.value); clearErrors(); }}
              rows={3}
              className={`w-full px-3 py-2 text-sm border rounded-lg resize-none ${
                errors.description ? 'border-danger-400 bg-danger-50' : 'border-neutral-300'
              }`}
              placeholder={`Describe what this ${type} is for`}
            />
            {errors.description && <p className="text-xs text-danger-500 mt-1">{errors.description}</p>}
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1.5">
              Tags <span className="text-danger-500">*</span>
            </label>
            <TagPicker
              value={tagIds}
              onChange={(ids) => { setTagIds(ids); clearErrors(); }}
              tags={tags}
              onTagsChanged={loadTags}
              placeholder="Add tag — type to search or create"
            />
            {errors.tagIds && <p className="text-xs text-danger-500 mt-1">{errors.tagIds}</p>}
          </div>

          {/* Duration (workflow only) */}
          {type === 'workflow' && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                Expected Duration <span className="text-danger-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={expectedDurationDays || ''}
                  onChange={e => {
                    setExpectedDurationDays(e.target.value ? parseInt(e.target.value, 10) : undefined);
                    clearErrors();
                  }}
                  className={`w-24 px-3 py-2 text-sm border rounded-lg ${
                    errors.duration ? 'border-danger-400 bg-danger-50' : 'border-neutral-300'
                  }`}
                  placeholder="—"
                />
                <span className="text-sm text-neutral-500">days</span>
              </div>
              <p className="text-xs text-neutral-400 mt-1">Used to suggest a due date when assigning this workflow</p>
              {errors.duration && <p className="text-xs text-danger-500 mt-1">{errors.duration}</p>}
            </div>
          )}

          {/* Asset requirement (workflow only) */}
          {type === 'workflow' && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                Asset Specific?
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setRequiresAsset(true)}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    requiresAsset
                      ? 'border-purple-400 bg-purple-50 text-purple-700 font-medium'
                      : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                  }`}
                >
                  Yes — runs per asset
                </button>
                <button
                  type="button"
                  onClick={() => setRequiresAsset(false)}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                    !requiresAsset
                      ? 'border-neutral-400 bg-neutral-100 text-neutral-700 font-medium'
                      : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                  }`}
                >
                  No — general workflow
                </button>
              </div>
              <p className="text-xs text-neutral-400 mt-1">
                {requiresAsset
                  ? 'Each assignment will be tied to a specific asset (company, fund, etc.)'
                  : 'Assignments will not require an asset'}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="sm">
              Create {label}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetupWizardModal;
